import React, { createContext, useContext, useMemo } from "react";
import axios, { AxiosInstance } from "axios";
// @ts-expect-error
import { REACT_APP_BACKEND_URL } from "@env";
import { getSecureStorage } from "../utils/storage";
import { useUserTimezone } from "../hooks/use-user-timezone";

type FetchContextState = {
  authRequest: AxiosInstance;
}

const FetchContext = createContext<FetchContextState | null>(null);

if (process.env.NODE_ENV === "development") {
  FetchContext.displayName = "FetchContext";
}

type FetchProviderProps = {
  children?: React.ReactNode;
}

const FetchProvider = ({ children }: FetchProviderProps): React.ReactElement => {
  const authRequest = axios.create({
    baseURL: REACT_APP_BACKEND_URL,
  });

  const { offset: minutesOffset } = useUserTimezone();

  const timezone = useMemo(() => {
    const offset = minutesOffset / 60;
    const minuteSide = offset % 1 !== 0 ? Math.abs(offset % 1) * 60 : 0;
    const hourSide = offset % 1 !== 0 ? Math.floor(offset) : offset;

    return `${hourSide >= 0 ? "+" : "-"}${Math.abs(hourSide) > 9 ? Math.abs(hourSide) : `0${Math.abs(hourSide)}`}:${
      minuteSide || "00"
    }`;
  }, [ minutesOffset ]);

  authRequest.interceptors.request.use(
    async (config) => ({
      ...config,
      headers: {
        Authorization : `Bearer ${(await getSecureStorage("user"))?.access_token}`,
        "x-timezone"  : timezone,
      },
    }),
    (error) => Promise.reject(error),
  );

  return <FetchContext.Provider value={{ authRequest }}>{children}</FetchContext.Provider>;
};

const useFetch = (): FetchContextState => {
  const context = useContext(FetchContext);

  if (!context) {
    throw new Error("useFetch must be used within a FetchProvider");
  }

  return context;
};

export { FetchProvider, useFetch };
